import {Title} from '@treez-ui/core/layout';
import {Grid} from '@material-ui/core';
import {Form, TextInput, TreezRadioGroup, TreezSwitch} from '@treez-ui/core/forms';
import React, {FunctionComponent, useEffect, useState} from 'react';
import PriceTierTable, {
    PriceTierThresholdRow,
    ThresholdRowKeys,
} from '@treez-ui/core/table/PriceTierTable';
import {
    PriceTier,
    PriceTierMethod,
    PriceTierThreshold,
    RangeMode,
} from '@treez/commons/sharedTypings/priceTier';
import _ from 'lodash';
import {TitleSize} from '@treez-ui/core/layout/Title';
import {formatInputAsString, formatInputNumberToString} from '@treez/commons/functions/formatter';
import {ModalType} from '@treez-ui/core/feedback/modals';
import {
    closeFeedbackModal,
    openFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';

export enum PriceTierEditMode {
    Edit,
    Add,
}

export interface EditPriceTierFormProps {
    priceTier?: PriceTier;
    onSave: (draftPriceTier?: Partial<PriceTier>) => void;
    onCancel: (draftPriceTier?: Partial<PriceTier>) => void;
    editMode: PriceTierEditMode;
}

interface EditedThresholds {
    [PriceTierMethod.WEIGHT]?: PriceTierThresholdRow[];
    [PriceTierMethod.BULK]?: PriceTierThresholdRow[];
    [PriceTierMethod.UNIT]?: PriceTierThresholdRow[];
}

const WEIGHT_PRICE_TIER_HEADERS = ['Start Weight (g)', 'Price (per size)', 'Price (per gram)'];

const UNIT_PRICE_TIER_HEADERS = ['Start Weight', 'Price (per number of units)', 'Price (per unit)'];

const BULK_METHOD_TIER_HEADERS = [
    'Weight (g)',
    'Tolerance Weight (g)',
    'Price (per size)',
    'Price (per gram)',
];

enum MethodSelect {
    Weight = 'Weight',
    DeliStyle = 'Deli Style (Bulk flower)',
    UnitBased = 'Unit Based',
}

const MethodSelectOptions = Object.keys(MethodSelect).map((key) => MethodSelect[key]);

enum PriceConfig {
    UsePreviousTier = 'Use price of previous tier',
    UseOneGramSize = 'Use Price of 1g size',
}

const PriceConfigOptions = Object.keys(PriceConfig).map((key) => PriceConfig[key]);

const mapMethodSelectToMethod = (methodSelect: MethodSelect) => {
    switch (methodSelect) {
        case MethodSelect.Weight:
            return PriceTierMethod.WEIGHT;
        case MethodSelect.DeliStyle:
            return PriceTierMethod.BULK;
        case MethodSelect.UnitBased:
            return PriceTierMethod.UNIT;
        default:
            return null;
    }
};

const mapMethodToMethodSelect = (method: PriceTierMethod) => {
    switch (method) {
        case PriceTierMethod.WEIGHT:
            return MethodSelect.Weight;
        case PriceTierMethod.BULK:
            return MethodSelect.DeliStyle;
        case PriceTierMethod.UNIT:
            return MethodSelect.UnitBased;
        default:
            return null;
    }
};

const mapPriceConfigToRangeMode = (priceConfig: PriceConfig) => {
    switch (priceConfig) {
        case PriceConfig.UsePreviousTier:
            return RangeMode.RANGE;
        case PriceConfig.UseOneGramSize:
            return RangeMode.FIXED_PRICE;
        default:
            return null;
    }
};

const mapRangeModeToPriceConfig = (rangeMode: RangeMode) => {
    switch (rangeMode) {
        case RangeMode.RANGE:
            return PriceConfig.UsePreviousTier;
        case RangeMode.FIXED_PRICE:
            return PriceConfig.UseOneGramSize;
        default:
            return null;
    }
};

const generateInitialThresholds = (method: PriceTierMethod): PriceTierThresholdRow[] => {
    switch (method) {
        case PriceTierMethod.WEIGHT:
            return [
                {quantityStart: '0.0', pricePer: '12.00', pricePerSize: ''},
                {quantityStart: '1.0', pricePer: '10.00', pricePerSize: '10'},
                {quantityStart: '3.5', pricePer: '8.00', pricePerSize: '28'},
                {quantityStart: '7.0', pricePer: '6.00', pricePerSize: '42'},
                {quantityStart: '14.0', pricePer: '5.50', pricePerSize: '77'},
                {quantityStart: '28.0', pricePer: '5.00', pricePerSize: '140'},
            ];
        case PriceTierMethod.UNIT:
            return [
                {quantityStart: '1', pricePer: '10.00', pricePerSize: '10'},
                {quantityStart: '3', pricePer: '8.00', pricePerSize: '24'},
                {quantityStart: '7', pricePer: '6.00', pricePerSize: '42'},
                {quantityStart: '14', pricePer: '5.50', pricePerSize: '77'},
                {quantityStart: '28', pricePer: '5.00', pricePerSize: '140'},
            ];
        case PriceTierMethod.BULK:
            return [
                {
                    quantityStart: '0.0',
                    tolerance: '0.2',
                    pricePer: '10.00',
                    pricePerSize: '10.00',
                    quantityEnd: '1.0',
                },
                {
                    quantityStart: '1.0',
                    tolerance: '0.2',
                    quantityEnd: '3.5',
                    pricePerSize: '10.00',
                    pricePer: '10.00',
                },
                {
                    quantityStart: '3.5',
                    tolerance: '0.2',
                    quantityEnd: '7.0',
                    pricePerSize: '28.00',
                    pricePer: '8.00',
                },
                {
                    quantityStart: '7.0',
                    tolerance: '0.2',
                    quantityEnd: '14.0',
                    pricePerSize: '42.00',
                    pricePer: '6.00',
                },
                {
                    quantityStart: '14.0',
                    tolerance: '0.2',
                    quantityEnd: '28.0',
                    pricePerSize: '77.00',
                    pricePer: '5.50',
                },
                {
                    quantityStart: '28.0',
                    tolerance: '0.2',
                    quantityEnd: '28.2',
                    pricePerSize: '140.00',
                    pricePer: '5.00',
                },
            ];
        default:
            return [];
    }
};

const isValidThresholds = (thresholds: PriceTierThreshold[], method: PriceTierMethod): boolean => {
    let previousThresholdMax;

    for (let i = 0; i < thresholds.length; i++) {
        const threshold = thresholds[i];
        if (method === PriceTierMethod.BULK) {
            if (previousThresholdMax && previousThresholdMax >= threshold.start) {
                return false;
            }

            if (_.isUndefined(threshold.tolerance) || _.isNull(threshold.tolerance)) return false;

            previousThresholdMax = threshold.start + threshold.tolerance;
        } else {
            if (previousThresholdMax && previousThresholdMax > threshold.start) {
                return false;
            }

            previousThresholdMax = threshold.start;
        }

        if (!isValidThreshold(threshold, thresholds.length - 1 === i, method)) {
            return false;
        }
    }

    return true;
};

const isValidThreshold = (
    threshold: PriceTierThreshold,
    isLast: boolean,
    method: PriceTierMethod
): boolean => {
    if (method === PriceTierMethod.WEIGHT) {
        if (
            threshold.start == null ||
            (isLast && threshold.end != null) ||
            threshold.value == null ||
            threshold.value == 0
        ) {
            return false;
        }
    } else if (method === PriceTierMethod.UNIT) {
        if (
            threshold.start == null ||
            (isLast && threshold.end != null) ||
            threshold.value == null ||
            threshold.value == 0
        ) {
            return false;
        }
    } else if (method === PriceTierMethod.BULK) {
        if (
            threshold.start == null ||
            (isLast && threshold.end != null) ||
            (threshold.start != 0 && threshold.tolerance == null) ||
            threshold.value == null ||
            threshold.value == 0
        ) {
            return false;
        }
    } else {
        return false;
    }

    return true;
};

const validatePriceTier = (priceTier: Partial<PriceTier>): string[] => {
    const errors: string[] = [];

    if (_.isUndefined(priceTier.label) || _.isNull(priceTier.label) || _.isEmpty(priceTier.label)) {
        errors.push('label');
    }

    if (_.isNull(priceTier.method) || _.isUndefined(priceTier.method)) {
        errors.push('method');
    }

    if (priceTier.method === PriceTierMethod.BULK && !_.has(priceTier, ['rangeMode'])) {
        errors.push('pricing config');
    }

    return errors;
};

export const EditPriceTierForm: FunctionComponent<EditPriceTierFormProps> = (
    props: EditPriceTierFormProps
) => {
    const {priceTier, onSave, onCancel, editMode} = props;

    const [priceTierToEdit, setPriceTierToEdit] = useState<Partial<PriceTier>>();
    const [label, setLabel] = useState<string>();
    const [isActive, setIsActive] = useState<boolean>(true);
    const [methodSelect, setMethodSelect] = useState<MethodSelect>();
    const [tierMethod, setTierMethod] = useState<PriceTierMethod>(PriceTierMethod.WEIGHT);
    const [editedThresholds, setEditedThresholds] = useState<EditedThresholds>({});
    const [tierThresholds, setTierThresholds] = useState<PriceTierThresholdRow[]>([]);
    const [priceConfig, setPriceConfig] = useState<PriceConfig>(PriceConfig.UsePreviousTier);

    useEffect(() => {
        if (editMode === PriceTierEditMode.Edit) {
            if (!_.isUndefined(priceTier)) {
                const initialThresholds = mapToThresholdRows(
                    priceTier.thresholds,
                    priceTier.method
                );
                setTierThresholds(initialThresholds);
                setEditedThresholds({
                    [priceTier.method]: initialThresholds,
                });
                setPriceConfig(mapRangeModeToPriceConfig(priceTier.rangeMode) as PriceConfig);
                setMethodSelect(mapMethodToMethodSelect(priceTier.method) as MethodSelect);
                setTierMethod(priceTier.method);
                setLabel(priceTier.label);
                setIsActive(priceTier.isActive);

                setPriceTierToEdit(priceTier);
            }
        } else if (editMode === PriceTierEditMode.Add) {
            setMethodSelect(mapMethodToMethodSelect(tierMethod) as MethodSelect);

            const initialThresholds = generateInitialThresholds(tierMethod);
            setTierThresholds(initialThresholds);
            setEditedThresholds({
                [tierMethod]: initialThresholds,
            });

            setPriceTierToEdit({
                label: '',
                isActive: true,
                method: tierMethod,
            });
        }
    }, [priceTier]);

    useEffect(() => {
        if (_.isUndefined(methodSelect)) {
            return;
        }

        // first save the current thresholds we have been working on.
        setEditedThresholds({
            ...editedThresholds,
            [tierMethod]: tierThresholds,
        });

        const method = mapMethodSelectToMethod(methodSelect) as PriceTierMethod;
        // fetch previously edited thresholds. If we haven't previously edited thresholds for this method. Generate a generic set.
        const previouslyEditedOrNewThresholds =
            editedThresholds[method] || generateInitialThresholds(method);
        setTierThresholds(previouslyEditedOrNewThresholds);
        setTierMethod(method);
    }, [methodSelect]);

    const onSaveWrapper = () => {
        const priceTierToSave = {
            ...priceTierToEdit,
            isActive,
            label,
            method: tierMethod,
            rangeMode: mapPriceConfigToRangeMode(priceConfig) as RangeMode,
            thresholds: mapToThresholds(tierThresholds),
        };

        const errors = validatePriceTier(priceTierToSave);
        if (errors.length > 0) {
            openFeedbackModal(
                () => {
                    closeFeedbackModal();
                },
                'Invalid Fields',
                {
                    type: ModalType.ERROR,
                    bodyText: `Please make sure that you fill in required fields: ${errors.join(
                        ','
                    )}`,
                    cancelOption: false,
                }
            );
        } else if (!isValidThresholds(priceTierToSave.thresholds, priceTierToSave.method)) {
            openFeedbackModal(
                () => {
                    closeFeedbackModal();
                },
                'Invalid Tier Table',
                {
                    type: ModalType.ERROR,
                    bodyText: `
                    Your tier table is invalid. Please make sure all quantities in each row
                    are greater than the previous row and all rows have a price greater than zero.
                    `,
                    cancelOption: false,
                }
            );
        } else {
            onSave(priceTierToSave);
        }
    };

    const onCancelWrapper = () => {
        const priceTierToCancel = {
            ...priceTierToEdit,
            isActive,
            label,
            method: tierMethod,
            rangeMode: mapPriceConfigToRangeMode(priceConfig) as RangeMode,
            thresholds: mapToThresholds(tierThresholds),
        };

        onCancel(priceTierToCancel);
    };

    const getTableHeaders = (): string[] => {
        switch (tierMethod) {
            case PriceTierMethod.WEIGHT:
                return WEIGHT_PRICE_TIER_HEADERS;
            case PriceTierMethod.BULK:
                return BULK_METHOD_TIER_HEADERS;
            case PriceTierMethod.UNIT:
                return UNIT_PRICE_TIER_HEADERS;
            default:
                return [];
        }
    };

    const getThresholdPricePerValue = (threshold: PriceTierThreshold): string => {
        const {value} = threshold;
        if (!_.isNaN(value) && value != Infinity) {
            return formatInputAsString(value, 4);
        }

        return '';
    };

    const getThresholdToleranceValue = (
        threshold: PriceTierThreshold,
        method: PriceTierMethod
    ): string => {
        const {tolerance} = threshold;

        if (method === PriceTierMethod.BULK && tolerance) {
            return formatInputAsString(tolerance, 4);
        }

        return '0.0';
    };

    const getThresholdQuantityStartValue = (threshold: PriceTierThreshold): string => {
        const {start} = threshold;

        return formatInputAsString(start, 4) || '';
    };

    const getThresholdQuantityEndValue = (threshold: PriceTierThreshold): string => {
        let {end} = threshold;

        end = end || 0;
        return formatInputAsString(end, 4) || '';
    };

    const getThresholdPricePerSizeValue = (threshold: PriceTierThreshold): string => {
        const {value, start} = threshold;

        const perSizeValue = value * start;
        if (!_.isNaN(perSizeValue) && perSizeValue !== Infinity) {
            return formatInputAsString(perSizeValue, 4);
        }
        return '';
    };

    const onThresholdRowValueChanged = (rowId: number, key: string, value: string | undefined) => {
        if (_.isUndefined(tierThresholds)) return;

        let threshold = {...tierThresholds[rowId]};
        value = value || '';

        if (tierMethod === PriceTierMethod.UNIT && key === ThresholdRowKeys.quantityStart) {
            threshold[key] = formatInputNumberToString(value, 0);
        } else {
            threshold[key] = value;
        }

        switch (key) {
            case ThresholdRowKeys.quantityStart:
                const pricePerSize = String(Number(value) * Number(threshold.pricePer));

                threshold = {
                    ...threshold,
                    pricePerSize,
                };
                break;
            case ThresholdRowKeys.pricePer:
                const pricePerSizeValue = String(Number(value) * Number(threshold.quantityStart));
                threshold = {
                    ...threshold,
                    pricePerSize: pricePerSizeValue,
                };
                break;
            case ThresholdRowKeys.pricePerSize:
                const pricePer = String(Number(value) / Number(threshold.quantityStart));
                threshold = {
                    ...threshold,
                    pricePer,
                };
            default:
                break;
        }

        const newThresholds = [...tierThresholds];
        newThresholds[rowId] = threshold;
        setTierThresholds(newThresholds);
    };

    const onAddThresholdRow = () => {
        const {quantityStart, pricePerSize, tolerance, pricePer} = ThresholdRowKeys;

        let thresholdRow;
        if (tierMethod === PriceTierMethod.BULK) {
            thresholdRow = {
                [quantityStart]: '0.0',
                [tolerance]: '0.0',
                [pricePerSize]: '0.0',
                [pricePer]: '0.00',
            };
        } else {
            thresholdRow = {
                [quantityStart]: '0',
                [pricePerSize]: '0.0',
                [pricePer]: '0.0',
            };
        }

        setTierThresholds([...tierThresholds, thresholdRow]);
    };

    const onRemoveThresholdRow = (rowId: number) => {
        const thresholds = [...tierThresholds];
        thresholds.splice(rowId, 1);
        setTierThresholds(thresholds);
    };

    const mapToThresholdRows = (
        thresholds: PriceTierThreshold[],
        method: PriceTierMethod
    ): PriceTierThresholdRow[] => {
        const {quantityStart, quantityEnd, pricePerSize, tolerance, pricePer} = ThresholdRowKeys;

        return thresholds.map((threshold) => ({
            [quantityStart]: getThresholdQuantityStartValue(threshold),
            [quantityEnd]: getThresholdQuantityEndValue(threshold),
            [pricePer]: getThresholdPricePerValue(threshold),
            [pricePerSize]: getThresholdPricePerSizeValue(threshold),
            [tolerance]: getThresholdToleranceValue(threshold, method),
        }));
    };

    const mapToThresholds = (thresholds: PriceTierThresholdRow[]): PriceTierThreshold[] => {
        return thresholds.map((threshold, ix) => {
            const {quantityStart, tolerance = '0', pricePer} = threshold;

            const mappedThreshold = {
                value: parseFloat(pricePer),
                start: parseFloat(quantityStart),
                end:
                    ix !== thresholds.length - 1
                        ? parseFloat(thresholds[ix + 1].quantityStart)
                        : null,
            };

            if (tierMethod === PriceTierMethod.BULK) {
                return {
                    ...mappedThreshold,
                    tolerance: parseFloat(tolerance),
                };
            }

            return mappedThreshold;
        });
    };

    return (
        <Form
            onSave={onSaveWrapper}
            onCancel={onCancelWrapper}
            title="Edit Price Tier"
            style={{backgroundColor: '#fff'}}
        >
            {priceTierToEdit && (
                <Grid container style={{marginTop: '100px'}}>
                    <Grid item xs={6}>
                        <div className="price-tier-details-wrapper">
                            <Title text="Pricing Details" size={TitleSize.MEDIUM} />
                            <Grid item xs={6}>
                                <div className="ept-details-section">
                                    <TextInput
                                        required
                                        onChange={(value) => setLabel(value)}
                                        label="label"
                                        value={label}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={6}>
                                <div className="ept-details-section">
                                    <Title text="Method*" size={TitleSize.MEDIUM} />
                                    <TreezRadioGroup
                                        onChange={(e) =>
                                            setMethodSelect(e.target.value as MethodSelect)
                                        }
                                        values={MethodSelectOptions}
                                        value={methodSelect}
                                    />
                                </div>
                            </Grid>
                            {tierMethod === PriceTierMethod.BULK && (
                                <Grid item xs={6}>
                                    <div className="ept-details-section">
                                        <Title text="Pricing Config" size={TitleSize.MEDIUM} />
                                        <TreezRadioGroup
                                            onChange={(e) =>
                                                setPriceConfig(e.target.value as PriceConfig)
                                            }
                                            values={PriceConfigOptions}
                                            value={priceConfig}
                                        />
                                    </div>
                                </Grid>
                            )}
                            <Grid item xs={6}>
                                <div className="ept-details-section">
                                    <TreezSwitch
                                        label="Active Price Tier"
                                        onChange={(e) => setIsActive(e.target.checked)}
                                        checked={isActive}
                                    />
                                </div>
                            </Grid>
                        </div>
                    </Grid>
                    <Grid item xs={6}>
                        <Title text="Pricing Table" size={TitleSize.MEDIUM} />
                        <PriceTierTable
                            headers={getTableHeaders()}
                            onRowAdded={onAddThresholdRow}
                            tierMethod={tierMethod}
                            onRowRemoved={onRemoveThresholdRow}
                            onThresholdValueChange={onThresholdRowValueChanged}
                            thresholds={tierThresholds}
                        />
                    </Grid>
                </Grid>
            )}
        </Form>
    );
};

export default EditPriceTierForm;
